import React from "react";
import { Box, Button, Popover, useMediaQuery } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs from "dayjs";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { styled } from "@mui/system";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// Custom styled PickersDay to highlight selected and hovered days
const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== "isSelected" && prop !== "isHovered" && prop !== "isOldData",
})(({ theme, isSelected, isHovered, day, isOldData }) => {
  // Determine week start/end based on whether date is in the past
  const weekStart = isOldData ? 0 : 1; // Sunday (0) for old, Monday (1) for new
  const weekEnd = isOldData ? 6 : 0;   // Saturday (6) for old, Sunday (0) for new
  
  return {
    borderRadius: 0,
    ...(isSelected && {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      "&:hover, &:focus": {
        backgroundColor: theme.palette.primary.main,
      },
    }),
    ...(isHovered && {
      backgroundColor: theme.palette.primary.light,
      "&:hover, &:focus": {
        backgroundColor: theme.palette.primary.light,
      },
    }),
    ...(day.day() === weekStart && {
      borderTopLeftRadius: "50%",
      borderBottomLeftRadius: "50%",
    }),
    ...(day.day() === weekEnd && {
      borderTopRightRadius: "50%",
      borderBottomRightRadius: "50%",
    }),
  };
});

// The week boundary moved from Sunday to Monday on 2025-10-12; ranges either
// side of that cutoff have to be labelled with the rule that was in force.
const WEEK_RULE_CUTOFF = "2025-10-12";

const weekBounds = (date) => {
  const d = dayjs(date);
  if (d.isBefore(dayjs(WEEK_RULE_CUTOFF), "day")) {
    const start = d.subtract(d.day(), "day").startOf("day");
    return [start, start.add(6, "day")];
  }
  return [d.startOf("week"), d.endOf("week")];
};

const formatWeekRange = (date) => {
  if (!date || !dayjs(date).isValid()) return "Select week";
  const [start, end] = weekBounds(date);
  const sameMonth = start.isSame(end, "month");
  return sameMonth
    ? start.format("D") + " – " + end.format("D MMM YYYY")
    : start.format("D MMM") + " – " + end.format("D MMM YYYY");
};

function WeekPicker({ selectedDate, onDateChange, mb = 2 }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [hoveredDay, setHoveredDay] = React.useState(null);
  // On a touch screen pointerenter fires as part of the tap. Setting hover
  // state re-rendered the day cells mid-gesture and the click was lost, so
  // selecting a week took two taps. Preview the range only where a real
  // pointer can hover.
  const canHover = useMediaQuery("(hover: hover) and (pointer: fine)");

  const isOpen = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDaySelect = (date) => {
    onDateChange(date);
    handleClose();
  };

  // Check if two dates are in the same week (considering legacy format for dates before Oct 10, 2025)
  const isSameWeek = (date1, date2) => {
    const d1 = dayjs(date1);
    const d2 = dayjs(date2);
    const cutoffDate = dayjs('2025-10-12');
    
    // For dates before October 12, 2025, use Sunday-based week
    const isOldData = d1.isBefore(cutoffDate, 'day') || d2.isBefore(cutoffDate, 'day');
    
    if (isOldData) {
      // Calculate week start as Sunday for both dates
      const week1Start = d1.subtract(d1.day(), 'day').startOf('day');
      const week2Start = d2.subtract(d2.day(), 'day').startOf('day');
      return week1Start.isSame(week2Start, 'day');
    }
    
    // For October 12, 2025 and after, use Monday-based week
    return d1.isSame(d2, 'week');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box display="flex" justifyContent="flex-end" mb={mb}>
        <Button
          variant="outlined"
          onClick={isOpen ? handleClose : handleOpen}
          aria-describedby={isOpen ? "week-picker-popover" : undefined}
          startIcon={<CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />}
          endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
          sx={{
            height: 38,
            minHeight: 38,
            fontWeight: 600,
            fontSize: "0.875rem",
            whiteSpace: "nowrap",
            color: "text.primary",
          }}
        >
          {formatWeekRange(selectedDate)}
        </Button>
      </Box>
      <Popover
        id="week-picker-popover"
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1.5,
            boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
            borderRadius: 1,
            maxWidth: "calc(100vw - 16px)",
            overflowX: "auto",
          },
        }}
      >
        <Box sx={{ p: 0 }}>
          <DateCalendar
            sx={{
              ".MuiDayCalendar-weekDayLabel": {
                color: "text.secondary",
              },
            }}
            value={selectedDate}
            onChange={handleDaySelect}
            showDaysOutsideCurrentMonth
            displayWeekNumber
            slots={{
              day: (props) => {
                const cutoffDate = dayjs('2025-10-12');
                const isOldData = dayjs(props.day).isBefore(cutoffDate, 'day');
                return (
                  <CustomPickersDay
                    {...props}
                    // 20px of side padding per day made the grid ~530px wide,
                    // far past a phone screen.
                    sx={{ px: { xs: 0, sm: 2.5 } }}
                    isOldData={isOldData}
                    isSelected={isSameWeek(props.day, selectedDate)}
                    isHovered={canHover && hoveredDay && isSameWeek(props.day, hoveredDay)}
                    onPointerEnter={canHover ? () => setHoveredDay(props.day) : undefined}
                    onPointerLeave={canHover ? () => setHoveredDay(null) : undefined}
                  />
                );
              },
            }}
          />
        </Box>
      </Popover>
    </LocalizationProvider>
  );
}

export default WeekPicker;
